import { useEffect, useState } from 'react';
import { useSectionTransition } from '../../../hooks/useSectionTransition';
import SearchOverlay from './SearchOverlay';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [loaded, setLoaded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { navigateToSection } = useSectionTransition();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 150);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMobileNavClick = (section: string) => {
    setMobileMenuOpen(false);
    navigateToSection(section);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#0F0F0F]/95 backdrop-blur-md shadow-lg shadow-black/40' : 'bg-transparent'
        } ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
      >
        <div className="px-4 md:px-8 lg:px-16">
          <div className="relative flex items-center justify-between h-16 md:h-24">
            <div className={`flex items-center gap-3 transition-all duration-700 ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`} style={{ transitionDelay: '300ms' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/7b868fb3-cec5-45b6-a480-a94a4b8cc3b7.png"
                alt="Southdev Home Depot"
                className="h-[60px] md:h-[82px] w-auto object-contain"
              />
              <img
                src="https://public.readdy.ai/ai/img_res/b275bc6b-97db-4827-8afc-1914ed379fd2.png"
                alt="Simply Elegant"
                className="h-[48px] md:h-[66px] w-auto mt-1 invert brightness-200 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]"
              />
            </div>

            <div className="hidden md:flex items-center space-x-10 absolute left-1/2 -translate-x-1/2">
              {['home', 'products'].map((section, i) => (
                <button
                  key={section}
                  onClick={() => navigateToSection(section)}
                  className={`text-white/80 hover:text-[#FF6600] transition-all duration-500 text-sm font-medium cursor-pointer whitespace-nowrap uppercase tracking-widest ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`}
                  style={{ transitionDelay: `${400 + i * 100}ms` }}
                >
                  {section}
                </button>
              ))}
            </div>

            <div
              className={`hidden md:flex items-center gap-5 transition-all duration-500 ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
              style={{ transitionDelay: '500ms' }}
            >
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-white/60 hover:text-[#FF6600] hover:bg-white/5 transition-all duration-300 cursor-pointer"
                aria-label="Search products"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              <button
                onClick={() => navigateToSection('contact')}
                className="text-white/80 hover:text-[#FF6600] transition-all duration-500 text-sm font-medium cursor-pointer whitespace-nowrap uppercase tracking-widest"
              >
                Contact Us
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full text-white/80 hover:text-[#FF6600] hover:bg-white/5 transition-all duration-300 cursor-pointer ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
              style={{ transitionDelay: '500ms' }}
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-16 left-0 right-0 bg-[#0F0F0F]/98 border-b border-white/10 transition-all duration-300 ${
            mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="px-4 py-6 space-y-1">
            {['home', 'products', 'contact'].map((section, i) => (
              <button
                key={section}
                onClick={() => handleMobileNavClick(section)}
                className={`w-full text-left text-white/80 hover:text-[#FF6600] hover:bg-white/5 transition-all duration-300 px-4 py-3 rounded-lg text-sm font-medium cursor-pointer uppercase tracking-widest ${
                  mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                {section === 'contact' ? 'Contact Us' : section}
              </button>
            ))}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setSearchOpen(true);
              }}
              className={`w-full text-left text-white/80 hover:text-[#FF6600] hover:bg-white/5 transition-all duration-300 px-4 py-3 rounded-lg text-sm font-medium cursor-pointer uppercase tracking-widest flex items-center gap-2 ${
                mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
              style={{ transitionDelay: '150ms' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line"></i>
              </div>
              Search
            </button>
          </div>
        </div>
      </div>

      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
